import React, { useEffect } from 'react';
import { Shield, Lock, Clock, AlertTriangle } from 'lucide-react';

interface SensitiveInfoModalProps {
    isOpen: boolean;
    detectedType: 'credit_card' | 'ssn' | 'password' | 'api_key' | 'phone' | null;
    isCurrentlyEncrypted: boolean;
    isCurrentlyExpiring: boolean;
    onConfirm: (encrypt: boolean, selfDestruct: boolean) => void;
    onDecline: () => void;
    onCancel: () => void;
}

export default function SensitiveInfoModal({
    isOpen,
    detectedType,
    isCurrentlyEncrypted,
    isCurrentlyExpiring,
    onConfirm,
    onDecline,
    onCancel
}: SensitiveInfoModalProps) {
    const [encrypt, setEncrypt] = React.useState(!isCurrentlyEncrypted);
    const [selfDestruct, setSelfDestruct] = React.useState(!isCurrentlyExpiring);

    React.useEffect(() => {
        if (isOpen) {
            // Default both to true as a strong recommendation, regardless of current message state
            setEncrypt(true);
            setSelfDestruct(true);
        }
    }, [isOpen, isCurrentlyEncrypted, isCurrentlyExpiring]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel();
        };
        if (isOpen) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    let warningText = "Sensitive information";
    if (detectedType === 'credit_card') warningText = "a Credit Card number";
    if (detectedType === 'ssn') warningText = "a Social Security Number";
    if (detectedType === 'password' || detectedType === 'api_key') warningText = "a Password or API Key";
    if (detectedType === 'phone') warningText = "a Phone Number";

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{
                maxWidth: 500,
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
                borderRadius: 'var(--radius-xl)',
                padding: '24px'
            }}>
                <div className="modal-header" style={{ borderBottom: 'none', padding: 0, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#f59e0b' }}>
                        <AlertTriangle size={28} />
                        <h2 className="modal-title" style={{ margin: 0, fontSize: 20, color: 'var(--text-primary)' }}>Sensitive Data Detected</h2>
                    </div>
                </div>

                <div className="modal-body" style={{ padding: 0 }}>
                    <p style={{ marginTop: 0, marginBottom: 24, color: 'var(--text-primary)', lineHeight: 1.6, fontSize: 15 }}>
                        We noticed this message might contain <span style={{ color: '#f59e0b', fontWeight: 600 }}>{warningText}</span>.
                        To protect your privacy and security, we strongly recommend encrypting this message and setting a self-destruct timer before sending.
                    </p>

                    <div style={{
                        background: 'var(--bg-secondary)',
                        padding: 20,
                        borderRadius: 'var(--radius-lg)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                        border: '1px solid var(--border-color)',
                        marginBottom: 24
                    }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={encrypt || isCurrentlyEncrypted}
                                disabled={isCurrentlyEncrypted}
                                onChange={(e) => setEncrypt(e.target.checked)}
                                style={{ marginTop: 6, width: 16, height: 16 }}
                            />
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>
                                    <Lock size={18} /> End-to-End Encrypt
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                                    {isCurrentlyEncrypted ? 'Already enabled for this message.' : 'Ensure only the recipient can read this message.'}
                                </div>
                            </div>
                        </label>

                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selfDestruct || isCurrentlyExpiring}
                                disabled={isCurrentlyExpiring}
                                onChange={(e) => setSelfDestruct(e.target.checked)}
                                style={{ marginTop: 6, width: 16, height: 16 }}
                            />
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>
                                    <Clock size={18} /> Enable Self-Destruct
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                                    {isCurrentlyExpiring ? 'Already enabled for this message.' : 'Automatically delete this message after it\'s read.'}
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 'none', padding: 0 }}>
                    <button className="btn btn-ghost" onClick={onCancel} style={{ padding: '8px 16px' }}>
                        Cancel
                    </button>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <button className="btn btn-secondary" onClick={onDecline} style={{ padding: '8px 20px' }}>
                            Send Anyway
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => onConfirm(encrypt, selfDestruct)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 24px', fontWeight: 600 }}
                        >
                            <Shield size={18} /> Send Securely
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
